import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge, Button, Card, CardContent, CardHeader, Flex, Heading, Text } from '@chatsdet/ui';
import { CalendarBlank, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useDigests } from '../../../features/digests/useDigests';
import { DIGEST_KIND_LABEL, digestWindowLabel } from '../../../features/digests/format';
import { DigestKpiStrip } from '../../components/digests/DigestKpiStrip';

/**
 * Home dashboard card previewing recent digests, index-based prev/next
 * through the newest ones (HintCards' interaction pattern, without the
 * auto-hide timer or dismiss — this card never auto-hides, see spec
 * decision 11). Renders nothing when the org genuinely has no digests yet;
 * shows a compact error line with a retry when the list fails to load. Leads
 * with the window date rather than the word "digest" — see `digestWindowLabel`.
 */
export function DigestsCard({ className }: { className?: string }) {
  const navigate = useNavigate();
  const digestsQuery = useDigests(1, 10);
  const [index, setIndex] = React.useState(0);

  const digests = digestsQuery.data?.data ?? [];

  if (digestsQuery.isLoading) return null;
  if (digestsQuery.isError) {
    return (
      <Card variant="bordered" padding="md" className={className}>
        <CardHeader>
          <Flex align="center" gap="sm">
            <CalendarBlank size={18} className="text-muted-foreground" />
            <Heading level={3}>Digests</Heading>
          </Flex>
        </CardHeader>
        <CardContent>
          <Flex align="center" justify="between" gap="md">
            <Text size="sm" className="text-muted-foreground">
              Unable to load digests right now.
            </Text>
            <Button variant="outline" size="sm" onPress={() => digestsQuery.refetch()}>
              Retry
            </Button>
          </Flex>
        </CardContent>
      </Card>
    );
  }
  if (digests.length === 0) return null;

  const clampedIndex = Math.min(index, digests.length - 1);
  const digest = digests[clampedIndex];
  if (!digest) return null;

  return (
    <Card variant="bordered" padding="md" className={className}>
      <CardHeader>
        <Flex justify="between" align="center">
          <Flex align="center" gap="sm" className="min-w-0">
            <CalendarBlank size={18} className="text-muted-foreground shrink-0" />
            <Heading level={3} className="truncate">
              {digestWindowLabel(digest.kind, digest.windowStart, digest.windowEnd, digest.timezone)}
            </Heading>
            <Badge variant={digest.kind === 'daily' ? 'default' : 'secondary'} className="shrink-0">
              {DIGEST_KIND_LABEL[digest.kind]}
            </Badge>
          </Flex>
          {digests.length > 1 && (
            <Flex align="center" gap="xs" className="shrink-0">
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                tooltip="Previous digest"
                isDisabled={clampedIndex === 0}
                onPress={() => setIndex((i) => Math.max(0, i - 1))}
              >
                <CaretLeft size={14} />
              </Button>
              <Text size="xs" className="text-muted-foreground tabular-nums">
                {clampedIndex + 1}/{digests.length}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                tooltip="Next digest"
                isDisabled={clampedIndex === digests.length - 1}
                onPress={() => setIndex((i) => Math.min(digests.length - 1, i + 1))}
              >
                <CaretRight size={14} />
              </Button>
            </Flex>
          )}
        </Flex>
      </CardHeader>
      <CardContent className="space-y-4">
        {digest.quiet ? (
          <Text size="sm" className="text-muted-foreground italic">
            {digest.kind === 'daily' ? 'Quiet day' : 'Quiet week'}: no runs, no Issues, no promoted Workflows,
            no Cofactor maintenance
          </Text>
        ) : (
          <>
            {digest.summaryMd ? (
              <Text size="sm" className="text-foreground">
                {digest.summaryMd}
              </Text>
            ) : (
              <Text size="sm" className="text-muted-foreground italic">
                Summary is still generating.
              </Text>
            )}
            <DigestKpiStrip headline={digest.headline} size="sm" />
          </>
        )}
        <Button variant="outline" size="sm" onPress={() => navigate({ to: `/digests/${digest.id}` })}>
          See details
        </Button>
      </CardContent>
    </Card>
  );
}
